import { Box, Button, Group, LoadingOverlay, Select, Stack, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import type { JSX } from "react";
import type { ItemRole, ItemType } from "../../utils";
import ToolArgumentsInput from "./ToolArgumentsInput";

export interface IFormValues {
    type: Exclude<ItemType, "REASONING">; // REASONING not addable/editable
    role: ItemRole; // used only when type === "MESSAGE"
    content: string; // required for MESSAGE and FUNCTION_CALL_OUTPUT
    toolCallId: string; // required for FUNCTION_CALL and FUNCTION_CALL_OUTPUT
    toolName: "" | "documentSearch" | "readDocument"; // required (non-empty) for FUNCTION_CALL
    toolArguments: Record<string, string>; // required (non-empty) for FUNCTION_CALL
}

interface IProps {
    handleSubmit: (values: IFormValues) => void;
    initialValues?: IFormValues;
    fetching: boolean;
}

const TYPES: Exclude<ItemType, "REASONING">[] = ["MESSAGE", "FUNCTION_CALL", "FUNCTION_CALL_OUTPUT"];
const ROLES: ItemRole[] = ["USER", "ASSISTANT"];
const TOOL_NAMES = ["documentSearch", "readDocument"] as const;

function ItemForm({ handleSubmit, initialValues, fetching }: IProps): JSX.Element {
    const form = useForm<IFormValues>({
        initialValues: initialValues ?? {
            type: "MESSAGE",
            role: "USER" as ItemRole,
            content: "",
            toolCallId: "",
            toolName: "",
            toolArguments: {},
        },
        validate: {
            type: (value) => (TYPES.includes(value) ? null : "Required"),
            role: (value, values) => {
                if (values.type === "MESSAGE") {
                    return ROLES.includes(value) ? null : "Required";
                }
                return null;
            },
            content: (value, values) => {
                if (values.type === "MESSAGE" || values.type === "FUNCTION_CALL_OUTPUT") {
                    return value ? null : "Required";
                }
                return null; // FUNCTION_CALL doesn't require content
            },
            toolName: (value, values) => {
                if (values.type === "FUNCTION_CALL") {
                    return value ? null : "Required";
                }
                return null;
            },
            toolCallId: (value, values) => {
                if (values.type === "FUNCTION_CALL" || values.type === "FUNCTION_CALL_OUTPUT") {
                    return value ? null : "Required";
                }
                return null;
            },
            toolArguments: (value, values) => {
                if (values.type === "FUNCTION_CALL") {
                    return Object.keys(value).length === 0 ? "Required" : null;
                }
                return null;
            },
        },
    });

    const showRole = form.values.type === "MESSAGE";
    const showContent = form.values.type === "MESSAGE" || form.values.type === "FUNCTION_CALL_OUTPUT";
    const showToolName = form.values.type === "FUNCTION_CALL";
    const showToolCallId = form.values.type === "FUNCTION_CALL" || form.values.type === "FUNCTION_CALL_OUTPUT";
    const showToolArguments = form.values.type === "FUNCTION_CALL";

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack maw={800} mx="auto">
                <LoadingOverlay visible={fetching} />
                <Select withAsterisk label="Item type" data={TYPES} {...form.getInputProps("type")} />
                {showRole && <Select withAsterisk label="Item role" data={ROLES} {...form.getInputProps("role")} />}
                {showContent && (
                    <Textarea
                        withAsterisk
                        autosize
                        minRows={3}
                        label="Item content"
                        {...form.getInputProps("content")}
                    />
                )}
                {showToolName && (
                    <Select withAsterisk label="Tool Name" data={TOOL_NAMES} {...form.getInputProps("toolName")} />
                )}
                {showToolCallId && (
                    <TextInput withAsterisk label="Tool Call ID" {...form.getInputProps("toolCallId")} />
                )}
                {showToolArguments && (
                    <Box>
                        <Box mb="xs">Tool Arguments</Box>
                        <ToolArgumentsInput
                            value={form.values.toolArguments}
                            onChange={(value) => form.setFieldValue("toolArguments", value)}
                        />
                    </Box>
                )}
                <Group justify="flex-end" mt="md">
                    <Button type="submit" disabled={fetching}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

export default ItemForm;
